import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useCustomers } from '../contexts/CustomersContext';
import { API_URL } from '../config';
import OrganizationNameForm from './OrganizationNameForm';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Grid,
  Avatar,
  Container,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  QrCode as QrCodeIcon,
  Subscriptions as SubscriptionsIcon,
  Help as HelpIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components for consistent design
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  transition: theme.transitions.create(['transform', 'box-shadow', 'border']),
  overflow: 'visible',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const CardContentArea = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const SectionAvatar = styled(Avatar)(({ theme, bgcolor }) => ({
  backgroundColor: bgcolor || theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  width: 40,
  height: 40,
}));

const AccountPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { isSubscriber, isLoading: isSubscriptionLoading, error: subscriptionError } = useSubscriptionStatus();
  const { getUserOrganization, isLoading: isCustomersLoading, error: customersError } = useCustomers();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const qrCanvasRef = useRef(null);

  const isLoading = isSubscriptionLoading || isCustomersLoading;
  const userOrg = getUserOrganization();

  const handleManageBilling = async () => {
    try {
      const token = await getAccessTokenSilently();

      const apiUrl = API_URL;
      const response = await fetch(`${apiUrl}/api/create-customer-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          return_url: window.location.href
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create customer portal session');
      }

      // Redirect to the Stripe Customer Portal
      window.location.href = data.url;

    } catch (err) {
      console.error('Error creating customer portal session:', err);
    }
  };

  const handleUpgradeClick = async () => {
    setIsUpgrading(true);
    try {
      // Check if we have user data
      if (!user || !user.email) {
        throw new Error('User data not available. Please try logging in again.');
      }

      console.log('Starting checkout process...');
      console.log('User data being sent:', {
        email: user.email,
        name: user.name
      });

      const response = await fetch(`${API_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          name: user.name || ''
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error response:', errorData);
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('Checkout response:', data);

      if (!data.url) {
        throw new Error(data.error || 'No checkout URL received');
      }

      window.location.assign(data.url);
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error.message || 'Failed to start checkout process');
    } finally {
      setIsUpgrading(false);
    }
  };

  // Function to generate QR code
  const generateQRCode = () => {
    if (!window.QRCode) {
      console.error('QRCode library not loaded');
      return;
    }

    if (!userOrg?.id) {
      console.error('Customer ID not available');
      return;
    }

    const streamUrl = `https://glossa.link/stream/${userOrg.id}`;
    const canvas = qrCanvasRef.current;
    
    if (canvas) {
      window.QRCode.toCanvas(canvas, streamUrl, { 
        width: 180,
        margin: 1,
        color: {
          dark: theme.palette.primary.main,
          light: '#ffffff'
        }
      }, function (error) {
        if (error) console.error('Error generating QR code:', error);
        else console.log('QR code generated for URL:', streamUrl);
      });
    }
  };

  const copyToClipboard = () => {
    if (!userOrg?.id) return;
    
    const streamUrl = `https://glossa.link/stream/${userOrg.id}`;
    navigator.clipboard.writeText(streamUrl)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  // Load the QR code library
  useEffect(() => {
    if (!window.QRCode) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/qrcode/build/qrcode.min.js';
      script.async = true;
      script.onload = () => {
        if (userOrg?.id && qrCanvasRef.current) {
          generateQRCode();
        }
      };
      document.body.appendChild(script);
    } else if (userOrg?.id && qrCanvasRef.current) {
      generateQRCode();
    }
  }, [userOrg?.id]);

  // Get organization name from user organization
  const organizationName = userOrg?.displayName || 'N/A';
  
  // Debug information
  console.log('AccountPage - User organization:', userOrg);
  console.log('AccountPage - Organization name:', organizationName);
  
  const handleOrgNameSuccess = (newName) => {
    // Force a page reload to refresh the data
    window.location.reload();
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          Please log in to view your account information.
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (subscriptionError || customersError) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          {subscriptionError || customersError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        Your Account
      </Typography>

      {/* User & Organization Information */}
      <StyledCard>
        <CardHeader
          avatar={
            <SectionAvatar>
              <PersonIcon />
            </SectionAvatar>
          }
          title="Profile Information"
          titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
        />
        <CardContentArea>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="body1">
                  <strong>Name:</strong> {user?.name}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="body1">
                  <strong>Email:</strong> {user?.email}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="body1">
                  <strong>Organization:</strong> {organizationName}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium' }}>
                    {organizationName === 'N/A' ? 'Add Organization Name' : 'Update Organization Name'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Your organization name identifies your broadcasts to listeners.
                  </Typography>
                  <OrganizationNameForm
                    currentOrganizationName={organizationName !== 'N/A' ? organizationName : ''}
                    onSuccess={handleOrgNameSuccess}
                    customerId={userOrg?.id || null}
                    userEmail={user?.email}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContentArea>
      </StyledCard>

      {/* Subscription Information */}
      <StyledCard>
        <CardHeader
          avatar={
            <SectionAvatar bgcolor={theme.palette.info.main}>
              <SubscriptionsIcon />
            </SectionAvatar>
          }
          title="Subscription Status"
          titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
        />
        <CardContentArea>
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ mb: isMobile ? 2 : 0 }}>
              {isSubscriber ? 
                'You have an active subscription to Glossa.link Broadcasting.' : 
                'You don\'t have an active subscription to Glossa.link Broadcasting.'}
            </Typography>
            
            {isSubscriber ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleManageBilling}
                startIcon={<SubscriptionsIcon />}
                sx={{ fontWeight: 500 }}
              >
                Manage Billing
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpgradeClick}
                disabled={isUpgrading}
                startIcon={isUpgrading ? <CircularProgress size={20} /> : <SubscriptionsIcon />}
                sx={{ fontWeight: 500 }}
              >
                {isUpgrading ? 'Processing...' : 'Subscribe Now'}
              </Button>
            )}
          </Box>
        </CardContentArea>
      </StyledCard>
      
      {/* QR Code Generator Section */}
      <StyledCard>
        <CardHeader
          avatar={
            <SectionAvatar bgcolor={theme.palette.success.main}>
              <QrCodeIcon />
            </SectionAvatar>
          }
          title="Streaming Link QR Code"
          titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
        />
        <CardContentArea>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" paragraph>
                Share this QR code for easy access to your broadcast stream. Users can scan it with their mobile device camera to listen to your broadcasts.
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={generateQRCode}
                  startIcon={<QrCodeIcon />}
                  disabled={!userOrg?.id}
                  sx={{ fontWeight: 500 }}
                >
                  Refresh QR Code
                </Button>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    https://glossa.link/stream/{userOrg?.id}
                  </Typography>
                  <Tooltip title={copySuccess ? "Copied!" : "Copy URL"}>
                    <IconButton
                      size="small"
                      onClick={copyToClipboard}
                      color={copySuccess ? "success" : "primary"}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {userOrg?.id ? (
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                  <canvas ref={qrCanvasRef} id="qrCanvas" />
                </Box>
              ) : (
                <Alert severity="warning" sx={{ width: '100%' }}>
                  Organization ID not available. Please ensure your organization is set up correctly.
                </Alert>
              )}
            </Grid>
          </Grid>
        </CardContentArea>
      </StyledCard>

      {/* Help & Support */}
      <StyledCard>
        <CardHeader
          avatar={
            <SectionAvatar bgcolor={theme.palette.warning.main}>
              <HelpIcon />
            </SectionAvatar>
          }
          title="Need Help?"
          titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
        />
        <CardContentArea>
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ mb: isMobile ? 2 : 0 }}>
              Have questions or need assistance with your account? Our support team is ready to help you.
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              href="mailto:support@glossa.link"
              startIcon={<HelpIcon />}
              sx={{ fontWeight: 500 }}
            >
              Contact Support
            </Button>
          </Box>
        </CardContentArea>
      </StyledCard>
    </Container>
  );
};

export default AccountPage;
